/**
 * BeforeAfterSection - Visual comparison: without NARA vs with NARA
 * Shows the pain points and how NARA solves them
 */
import React from 'react';
import { Typography, Box, Container } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import PhoneMissedIcon from '@mui/icons-material/PhoneMissed';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import SmartToyIcon from '@mui/icons-material/SmartToy';

import { COLORS, TYPOGRAPHY, GRADIENTS } from './landingTheme';
import { ScrollReveal } from './animations';

const BEFORE_ITEMS = [
  { icon: <PhoneMissedIcon />, text: 'Llamadas perdidas fuera de horario' },
  { icon: <EventBusyIcon />, text: 'Citas agendadas solo por teléfono' },
  { icon: <PersonOffIcon />, text: 'Pacientes sin seguimiento continuo' },
  { icon: <DescriptionIcon />, text: 'Historial disperso en papel y notas' },
  { icon: <AccessTimeIcon />, text: 'Doctor responde todo personalmente' },
];

const AFTER_ITEMS = [
  { icon: <WhatsAppIcon />, text: 'Atención automática 24/7 por WhatsApp' },
  { icon: <EventAvailableIcon />, text: 'Agenda inteligente con IA' },
  { icon: <PsychologyIcon />, text: 'Memoria semántica de cada paciente' },
  { icon: <FolderSharedIcon />, text: 'Expediente médico digital integrado' },
  { icon: <SmartToyIcon />, text: 'IA filtra y escala solo lo necesario' },
];

const ComparisonColumn = ({ title, subtitle, items, isAfter }) => (
  <Box
    sx={{
      flex: 1,
      p: { xs: 3, md: 4 },
      borderRadius: '20px',
      background: isAfter
        ? 'linear-gradient(135deg, #0f2440 0%, #1e3a5f 100%)'
        : COLORS.white,
      border: isAfter ? 'none' : `1px solid ${COLORS.gray200}`,
      boxShadow: isAfter
        ? '0 20px 60px rgba(15, 36, 64, 0.3)'
        : '0 4px 20px rgba(0,0,0,0.06)',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    {/* Decorative glow for "After" */}
    {isAfter && (
      <Box
        sx={{
          position: 'absolute',
          top: -40,
          right: -40,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(245, 158, 11, 0.12) 0%, transparent 60%)',
          pointerEvents: 'none',
        }}
      />
    )}

    {/* Header */}
    <Box sx={{ mb: 3, position: 'relative' }}>
      <Typography
        sx={{
          fontSize: '0.7rem',
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: isAfter ? '#fbbf24' : '#ef4444',
          mb: 0.5,
        }}
      >
        {subtitle}
      </Typography>
      <Typography
        sx={{
          fontSize: TYPOGRAPHY.h3,
          fontWeight: 800,
          color: isAfter ? '#fff' : COLORS.textPrimary,
        }}
      >
        {title}
      </Typography>
    </Box>

    {/* Items */}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {items.map((item, idx) => (
        <Box
          key={idx}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          {/* Status icon */}
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: isAfter
                ? 'rgba(245, 158, 11, 0.15)'
                : 'rgba(239, 68, 68, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '& svg': {
                fontSize: 16,
                color: isAfter ? '#fbbf24' : '#ef4444',
              },
            }}
          >
            {isAfter ? <CheckIcon /> : <CloseIcon />}
          </Box>

          {/* Feature icon + text */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Box
              sx={{
                '& svg': {
                  fontSize: 18,
                  color: isAfter ? 'rgba(255,255,255,0.5)' : COLORS.gray400,
                },
                flexShrink: 0,
              }}
            >
              {item.icon}
            </Box>
            <Typography
              sx={{
                fontSize: TYPOGRAPHY.small,
                color: isAfter ? 'rgba(255,255,255,0.85)' : COLORS.textSecondary,
                lineHeight: 1.5,
              }}
            >
              {item.text}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>

    {/* Bottom accent for "After" */}
    {isAfter && (
      <Box
        sx={{
          mt: 3,
          pt: 2.5,
          borderTop: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#25D366',
            boxShadow: '0 0 8px rgba(37, 211, 102, 0.5)',
          }}
        />
        <Typography
          sx={{
            fontSize: '0.8rem',
            color: 'rgba(255,255,255,0.6)',
            fontWeight: 500,
          }}
        >
          Activo 24/7 — respondiendo en menos de 2 segundos
        </Typography>
      </Box>
    )}
  </Box>
);

export default function BeforeAfterSection() {
  return (
    <Box
      sx={{
        py: { xs: 8, md: 12 },
        background: COLORS.bgGray,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <ScrollReveal>
          <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: COLORS.accent,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                mb: 1.5,
              }}
            >
              El impacto
            </Typography>
            <Typography
              sx={{
                fontSize: TYPOGRAPHY.h2,
                fontWeight: 800,
                color: COLORS.textPrimary,
                letterSpacing: '-0.02em',
                mb: 1.5,
              }}
            >
              Antes y después de{' '}
              <Box
                component="span"
                sx={{
                  background: GRADIENTS.textGradient,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                NARA
              </Box>
            </Typography>
            <Typography
              sx={{
                fontSize: TYPOGRAPHY.body,
                color: COLORS.textSecondary,
                maxWidth: 520,
                mx: 'auto',
                lineHeight: 1.7,
              }}
            >
              Descubre cómo NARA transforma la operación diaria de tu consultorio médico.
            </Typography>
          </Box>
        </ScrollReveal>

        {/* Comparison columns */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 3, md: 4 },
            maxWidth: 900,
            mx: 'auto',
          }}
        >
          <ScrollReveal direction="left" delay={0}>
            <ComparisonColumn
              title="Sin NARA"
              subtitle="Método tradicional"
              items={BEFORE_ITEMS}
              isAfter={false}
            />
          </ScrollReveal>
          <ScrollReveal direction="right" delay={0.15}>
            <ComparisonColumn
              title="Con NARA"
              subtitle="Asistente médico IA"
              items={AFTER_ITEMS}
              isAfter={true}
            />
          </ScrollReveal>
        </Box>
      </Container>
    </Box>
  );
}
